import { chainsApi } from '@/api/chains'
import type {
  CreateChainDefinitionRequest,
  PaginationParams,
  UpdateChainDefinitionRequest,
  ChainDefinitionFilters,
  WorkflowValidationResult,
  StartChainRequest,
} from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenantKey } from './useTenantKey'

const ALL_CHAINS_PAGINATION: PaginationParams = { skipCount: 0, maxResultCount: 10000 }

export function useChains(projectId: string, pagination?: PaginationParams, filters?: ChainDefinitionFilters) {
  const tenantKey = useTenantKey()
  const pag = pagination ?? ALL_CHAINS_PAGINATION
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chains', { ...filters, ...pag }),
    queryFn: () => chainsApi.getAll(projectId, filters, pag),
    enabled: !!projectId,
  })
}

export function useChain(projectId: string, id: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chains', id),
    queryFn: () => chainsApi.getById(projectId, id),
    enabled: !!projectId && !!id,
  })
}

export function useCreateChain(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (data: CreateChainDefinitionRequest) => chainsApi.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chains') })
    },
  })
}

export function useUpdateChain(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateChainDefinitionRequest }) =>
      chainsApi.update(projectId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chains') })
    },
  })
}

export function useDuplicateChain(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (id: string) => chainsApi.duplicate(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chains') })
    },
  })
}

export function useStartChain(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StartChainRequest }) => chainsApi.start(projectId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chain-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
  })
}

export function useToggleChainActive(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (id: string) => chainsApi.toggleActive(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chains') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'settings', 'azure-devops-ingestion') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-ingestion-status') })
    },
  })
}

export function useDeleteChain(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (id: string) => chainsApi.delete(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chains') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'settings', 'azure-devops-ingestion') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items', 'azure-devops-ingestion-status') })
    },
  })
}

export function useValidateChainYaml(projectId: string) {
  return useMutation<WorkflowValidationResult, Error, string>({
    mutationFn: (yamlContent: string) => chainsApi.validateYaml(projectId, yamlContent),
  })
}

export function useChainYamlSnapshots(projectId: string, chainId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chains', chainId, 'yaml-snapshots'),
    queryFn: () => chainsApi.getYamlSnapshots(projectId, chainId!),
    enabled: !!projectId && !!chainId,
  })
}
